import React, { Component } from 'react';
import {
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaUserCheck,
  FaUserPlus,
} from 'react-icons/fa';
import { getBundleColor, getBundleFontColor } from '../utils/bundleDraft';
import './PlannerBundle.scss';
import { Link } from 'react-router-dom';
import {
  BundleJson,
  BundleStatus,
  Instruction,
  InstructionType,
} from '../../../types/bundleTypes';
import { classNames } from '../../../utils/classUtils';
import { OrderInstruction } from '../../../components/maps/BundleMap';
import { Order } from '../../../types/orderTypes';
import { CourierVehicle } from '../../couriers/courierVehicle';
import dayjs from 'dayjs';
import MoreActionMenu from '../../../components/MoreActionMenu/MoreActionMenu';
import 'react-popper-tooltip/dist/styles.css';
import { MdCallSplit } from 'react-icons/all';
import { PopoverComponent } from '../../../ReusableComponents/Popover/PopoverComponent';

interface Props {
  onExpand?: (bundle: BundleJson, expanded: boolean) => void;
  expanded: boolean;
  monochrome?: boolean;
  monitorView?: boolean;
  bundle: BundleJson;
  orderInstructions: OrderInstruction[];
  recreateModeActive: boolean;
  isPopoverOpen: boolean;
  toggleExpanded: () => void;
  onClickShowCourierModal: () => void;
  startReordering: () => void;
  stopReordering: () => void;
  handlePopoverClick: () => void;
  onSelectBundleDraft?: (id: string) => void;
  bundleDraftIsSelected: boolean;
  onDeselectBundleDraft?: (id: string) => void;
  startRecreating: () => void;
  stopRecreating: () => void;
  onClickShowMap: () => void;
  orders: Order[];
  completedStatusList?: boolean;
  startMoving: () => void;
  stopMoving: () => void;
  movingToBundleActive: boolean;
  setZonesOfBundles?: (newZonesOfBundles: {
    [bundleId: string]: string;
  }) => void;
  onClickShowPickupPointModal: () => void;
}

export default class PlannerBundlePlate extends Component<Props> {
  state = {
    anchor: null,
  };
  bundleTitle = (bundle: BundleJson): string => {
    switch (bundle.status) {
      case BundleStatus.Accepted:
        return `In delivery:`;
      case BundleStatus.Draft:
        return 'Draft:';
      case BundleStatus.Proposed:
        return `Proposed:`;
      case BundleStatus.Published:
        return 'Published';
      case BundleStatus.Completed:
        return 'Completed';
      default:
        throw new Error(`Unexpected bundle status: ${bundle.status}`);
    }
  };

  setAnchor = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchor: event.currentTarget });
  };

  onClosePopover = () => this.setState({ anchor: null });

  getTheZonesInfo = (): string => {
    const { orders, bundle, setZonesOfBundles } = this.props;
    if (orders.length === 0) {
      return '-';
    }
    const zones = Array.from(
      new Set(orders.map(order => order?.zone?.split('_')[0] || '-'))
    );
    const result = zones.sort().join(', ');
    setZonesOfBundles({ [bundle.id]: result });
    return result;
  };

  monitorViewPlate = (
    isDraft: boolean,
    ordersWithDeviations: string[],
    bundleInstructions: Instruction[],
    bundleTitle: string,
    isPublished: boolean,
    isCompleted: boolean,
    ordersCompleted: number,
    color: string,
    completedPickups: Instruction[],
    anchor: HTMLElement,
    onClickShowMap: () => void
  ): JSX.Element => {
    const {
      expanded,
      bundle,
      isPopoverOpen,
      toggleExpanded,
      onClickShowCourierModal,
      handlePopoverClick,
      completedStatusList,
      orders,
    } = this.props;
    return (
      <div
        className={classNames(
          { 'planner-bundle-title__expanded': !expanded },
          'planner-bundle-title'
        )}
        onClick={toggleExpanded}
      >
        <div
          className={classNames(
            { 'monitor-bundle-plate-grid-completed': completedStatusList },
            'monitor-bundle-plate-grid'
          )}
        >
          <div className="planner-bundle-plate-values">
            <div className="planner-bundle-plate-courier-value tooltip">
              {(bundle.assignedCourierId && (
                <div>
                  <p className="tooltiptext"> {bundle.assignedCourierName}</p>
                  <div className="text-ellipsis-wrapper">
                    <span
                      className="span-courier"
                      onClick={event => {
                        event.stopPropagation();
                        onClickShowCourierModal();
                      }}
                    >
                      {bundle.assignedCourierName}
                    </span>
                  </div>
                </div>
              )) || <span>-</span>}
            </div>
            <div className="planner-bundle-plate-vehicle-value">
              {(bundle.assignedCourierId && (
                <CourierVehicle
                  courier={{
                    travelMode: bundle.travelMode,
                    carType: bundle.carType,
                  }}
                />
              )) || <span>-</span>}
            </div>
            <div className="planner-bundle-plate-pickup-value">
              {completedPickups.length}
              {bundleInstructions.length / 2 - completedPickups.length === 0
                ? '-'
                : '/'}
              {bundleInstructions.length / 2}
            </div>
            <div className="planner-bundle-plate-zone-value">
              <span>{this.getTheZonesInfo()}</span>
            </div>
            <div className="planner-bundle-plate-deviations-value">
              {(ordersWithDeviations.length > 0 && (
                <>
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      this.setAnchor(e);
                      handlePopoverClick();
                    }}
                    className="planner-deviations"
                  >
                    <FaExclamationTriangle
                      style={{ paddingTop: '4px', color: 'orange' }}
                    />{' '}
                    {ordersWithDeviations.length}
                  </span>
                  <PopoverComponent
                    id={bundle.id}
                    anchorEl={anchor}
                    open={isPopoverOpen}
                    onClose={this.onClosePopover}
                  >
                    <div
                      className="planer-popover-content"
                      onClick={e => e.stopPropagation()}
                    >
                      {ordersWithDeviations.map(
                        (orderId: string, index: number) => (
                          <Link
                            className="problem-order-id"
                            key={index}
                            to={{ pathname: `/orders/${orderId}` }}
                          >
                            {orderId}
                          </Link>
                        )
                      )}
                    </div>
                  </PopoverComponent>
                </>
              )) || <span>0</span>}
            </div>
            <div className="planner-bundle-plate-deliveries-value">
              <span>
                {!isPublished && !isCompleted && bundleInstructions.length / 2}
                {(isPublished || isCompleted) &&
                  `${ordersCompleted} / ${bundleInstructions.length / 2}`}
              </span>
            </div>
            <div className="planner-bundle-plate-bundle-value">
              {(bundle && bundle.id && (
                <Link
                  to={`/bundles/${bundle.id}`}
                  className="link__clean"
                  target={'_blank'}
                  style={{
                    color: color,
                    fontSize: '1.2rem',
                    paddingRight: '5px',
                  }}
                >
                  <span>{bundle.id}</span>
                </Link>
              )) || <span>-</span>}
            </div>
            <div className="planner-bundle-plate-shelf-value">
              {(bundle.shelfId && <span>{bundle.shelfId}</span>) || (
                <span>-</span>
              )}
            </div>
            <div className="planner-bundle-plate-departure-value">
              {completedStatusList ? (
                <span>
                  {dayjs(orders[orders.length - 1]?.deliveredAt).format(
                    'YYYY-MM-DD'
                  )}
                </span>
              ) : (
                (bundle.instructions[0]?.eta &&
                  bundle.instructions[0]?.eta?.isValid() && (
                    <span>{bundle.instructions[0].eta.format('HH:mm')}</span>
                  )) || <span>-</span>
              )}
            </div>
          </div>
          {completedStatusList ? (
            <div className="planner-bundle-plate-icons">
              <div className="planner-bundle-action-icon">
                <FaMapMarkerAlt
                  onClick={event => {
                    event.stopPropagation();
                    onClickShowMap();
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="planner-bundle-plate-icons">
              {this.renderIconSection()}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderIconSection = (): JSX.Element => {
    const {
      recreateModeActive,
      onClickShowCourierModal,
      startRecreating,
      stopRecreating,
      onClickShowMap,
      bundle,
      movingToBundleActive,
      startMoving,
      stopMoving,
    } = this.props;
    const actionList = [
      {
        text: 'Move to bundle',
        clickAction: !movingToBundleActive ? startMoving : stopMoving,
      },
    ];
    return (
      <>
        <div className="planner-bundle-action-icon">
          <FaMapMarkerAlt
            onClick={event => {
              event.stopPropagation();
              onClickShowMap();
            }}
          />
        </div>
        <div className="planner-bundle-action-icon">
          {bundle.assignedCourierId && (
            <FaUserCheck
              onClick={event => {
                event.stopPropagation();
                onClickShowCourierModal();
              }}
            />
          )}
          {!bundle.assignedCourierId && (
            <FaUserPlus
              onClick={event => {
                event.stopPropagation();
                onClickShowCourierModal();
              }}
            />
          )}
        </div>
        <div
          className={classNames(
            {
              'planner-bundle-action-icon--active': recreateModeActive,
            },
            'planner-bundle-action-icon'
          )}
        >
          <MdCallSplit
            onClick={event => {
              event.stopPropagation();
              if (!recreateModeActive) {
                startRecreating();
              } else {
                stopRecreating();
              }
            }}
          />
        </div>
        <MoreActionMenu actionList={actionList} menuSide="left" />
      </>
    );
  };

  render(): JSX.Element {
    const {
      expanded,
      monochrome,
      monitorView,
      bundle,
      toggleExpanded,
      onClickShowCourierModal,
      onSelectBundleDraft,
      bundleDraftIsSelected,
      onDeselectBundleDraft,
      orders,
      onClickShowMap,
      recreateModeActive,
      movingToBundleActive,
      startRecreating,
      stopRecreating,
      startMoving,
      stopMoving,
      onClickShowPickupPointModal,
    } = this.props;
    const { anchor } = this.state;
    const bundleInstructions = bundle.instructions;
    const ordersCompleted = bundle.instructions.filter(
      instr => instr.type === InstructionType.Delivery && instr.completed
    ).length;
    const completedPickups = bundle.instructions.filter(
      pickup => pickup.type === InstructionType.Pickup && pickup.completed
    );
    const bundleTitle = this.bundleTitle(bundle);
    const isDraft = bundle.status === BundleStatus.Draft;
    const isPublished = [
      BundleStatus.Published,
      BundleStatus.Proposed,
      BundleStatus.Accepted,
    ].includes(bundle.status);
    const isCompleted = [BundleStatus.Completed].includes(bundle.status);
    const color = monochrome ? '#202020' : getBundleFontColor(bundle.id);
    const backgroundColor = monochrome ? '#fbfbfb' : getBundleColor(bundle.id);
    const ordersWithDeviations = Array.from(
      new Set(
        orders
          .filter(order => order.openCourierDeviation)
          .map(order => order.id)
      )
    );
    const actionList = [
      {
        text: 'Assign courier',
        clickAction: onClickShowCourierModal,
      },
      {
        text: 'Split bundle',
        clickAction: !recreateModeActive ? startRecreating : stopRecreating,
      },
      {
        text: 'Move to bundle',
        clickAction: !movingToBundleActive ? startMoving : stopMoving,
      },
      {
        text: 'Set pickup point',
        clickAction: onClickShowPickupPointModal,
      },
    ];
    return (
      <div
        style={{
          backgroundColor: monitorView ? backgroundColor : null,
          // color: color,
          border: '1px solid #ccc',
        }}
        className="planner-bundle-header"
      >
        {monitorView ? (
          this.monitorViewPlate(
            isDraft,
            ordersWithDeviations,
            bundleInstructions,
            bundleTitle,
            isPublished,
            isCompleted,
            ordersCompleted,
            color,
            completedPickups,
            anchor,
            onClickShowMap
          )
        ) : (
          <>
            <div
              style={{ backgroundColor: backgroundColor }}
              className="planner-bundle-title-background"
              onClick={e => e.stopPropagation()}
            >
              <input
                id={`bundleTitle${bundle.id}`}
                type="checkbox"
                checked={bundleDraftIsSelected}
                onChange={event =>
                  event.target.checked
                    ? onSelectBundleDraft && onSelectBundleDraft(bundle.id)
                    : onDeselectBundleDraft(bundle.id)
                }
              />
              <Link
                to={`/bundles/${bundle.id}`}
                className="link__clean"
                target={'_blank'}
                style={{ color }}
              >
                <span onClick={e => e.stopPropagation()}>{bundle.id}</span>
              </Link>
            </div>
            <div
              className={classNames(
                { 'planner-bundle-title__expanded': !expanded },
                'planner-bundle-title'
              )}
              onClick={toggleExpanded}
            >
              <div className="planner-bundle-title-line">
                <div className="planner-bundle-title-line-orders">
                  <span>
                    Orders: {!isPublished && bundleInstructions.length / 2}
                    {isPublished &&
                      `${ordersCompleted} / ${bundleInstructions.length / 2}`}
                  </span>
                </div>
                {bundle.assignedCourierId ? (
                  <div className="planner-bundle-title-line-courier tooltip">
                    <p className="tooltiptext">{bundle.assignedCourierName}</p>
                    <div className="text-ellipsis-wrapper">
                      <span
                        onClick={event => {
                          event.stopPropagation();
                          onClickShowCourierModal();
                        }}
                      >
                        <CourierVehicle
                          courier={{
                            travelMode: bundle.travelMode,
                            carType: bundle.carType,
                          }}
                        />{' '}
                        {bundle.assignedCourierName}
                      </span>
                    </div>
                  </div>
                ) : null}
                <div
                  className={
                    !bundle.assignedCourierId
                      ? 'planner-bundle-title-line-icons no-courier'
                      : 'planner-bundle-title-line-icons'
                  }
                >
                  {!bundle.assignedCourierId ? (
                    <div
                      className="planner-bundle-title-line-courier"
                      onClick={event => {
                        event.stopPropagation();
                        onClickShowCourierModal();
                      }}
                    >
                      <FaUserCheck />
                    </div>
                  ) : null}
                  <div
                    className="planner-bundle-title-line-map"
                    onClick={event => {
                      event.stopPropagation();
                      onClickShowMap();
                    }}
                  >
                    <FaMapMarkerAlt />
                  </div>
                  <MoreActionMenu
                    className="planner-bundle-title-line-more-menu"
                    actionList={actionList}
                    menuSide="left"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}
